import { Component, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef, Injector } from '@angular/core';
import { DateTime } from 'luxon';
import { CsCallablePage } from 'providers/nav-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { DebugService } from 'services/debug.service';
import { SettingsService } from 'services/settings.service';
import { TaskStatus } from 'components/cs-task-calendar-day/cs-task-calendar-day';
import { CsContentComponent } from 'components/cs-content/cs-content';
import { waitForAngularUpdate } from 'services/browser-utils';
import { ScrollHideService } from 'services/scroll-hide.service';
import { PubSubRegistration, PubSubService } from 'services/pubsub.service';
import { LoggingService, errorReportFields } from 'services/logging.service';
import { isDatabaseTornDown } from 'services/database-unavailable-error';
import { TaskStatusCacheUpdatedEvent } from 'services/user-db.service';
import { taskStatusChangeAffectsVisibleDays, taskStatusChangedSpans, TaskStatusChangeRange } from 'services/user-db.task-status';
import { TaskCalendarPageParams, TaskCalendarPageResult } from './task-calendar-interface';

import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { formatMonthLong } from '@cf-shared/date-format';
import { formatMonthKey } from '@cf-shared/date-format-machine';
interface CalendarDay {
  date: DateTime;
  // the day's ISO date key, computed once when the cell is built. Kept as a
  // field rather than calling toISODate() from the template, which would run on
  // every change-detection pass for every visible cell.
  dateKey: string;
  taskStatus: TaskStatus;
  isSelected: boolean;
  isToday: boolean;
  isOutOfMonth: boolean;
}

interface CalendarWeek {
  days: CalendarDay[];
}

interface CalendarMonth {
  monthId: string; // unique identifier for the month (e.g., '2024-06')
  month: DateTime; // first day of the month
  monthName: string; // e.g., 'June'
  year: number;
  weeks: CalendarWeek[];
}

@Component({
  selector: 'page-task-calendar',
  templateUrl: 'task-calendar-page.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ModalPageMarker]
})
export class TaskCalendarPage extends CsCallablePage<TaskCalendarPageParams, TaskCalendarPageResult> {
  protected LL = LL;
  @ViewChild(CsContentComponent) csContent: CsContentComponent;
  @ViewChild('topSentinel', { read: ElementRef }) topSentinel: ElementRef<HTMLElement>;
  @ViewChild('bottomSentinel', { read: ElementRef }) bottomSentinel: ElementRef<HTMLElement>;

  title: string = 'Select Date';
  description: string = '';
  selectedDate: DateTime;
  private taskStatusLoader?: (startDate: DateTime, endDate: DateTime) => Promise<Map<string, TaskStatus>>;
  private taskStatusCache: Map<string, TaskStatus> = new Map();

  months: CalendarMonth[] = [];
  // ISO weekday order: Mon=0, Tue=1, Wed=2, Thu=3, Fri=4, Sat=5, Sun=6
  readonly dayLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  loading = true;

  // Flags for showing loading sentinels
  canLoadEarlier = true;
  canLoadLater = true;

  // Track the range of loaded months
  earliestMonth: DateTime;
  latestMonth: DateTime;

  // Number of months to load initially
  private readonly INITIAL_MONTHS_TO_LOAD = 12;

  // Pre-loading state for optimization
  private essentialLoadingPromise: Promise<void> | null = null;
  private essentialStart: DateTime;
  private essentialEnd: DateTime;

  private isLoadingMore = false;
  private intersectionObserver: IntersectionObserver;

  // #4249: this page used to refresh its dots on page entry only, so a change
  // synced in from another device never showed while the month grid was open.
  private taskStatusCacheUpdatedReg: PubSubRegistration;

  constructor(
    private debug: DebugService,
    private settings: SettingsService,
    private cd: ChangeDetectorRef,
    private injector: Injector,
    private scrollHideService: ScrollHideService,
    private pubSub: PubSubService,
    private logging: LoggingService
  ) {
    super();
  }

  // returns ISO indices in display order based on user's first day of week preference
  get displayOrder(): number[] {
    return this.settings.getWeekDayDisplayOrder();
  }

  // returns weekday labels in display order
  get weekdayLabels(): string[] {
    return this.displayOrder.map(isoIdx => this.dayLabels[isoIdx]);
  }

  // gets the first day of the display week (1=Mon, 6=Sat, 7=Sun in Luxon format)
  private getFirstDayOfWeek(): number {
    const displayOrder = this.settings.getWeekDayDisplayOrder();
    // displayOrder[0] is the ISO index (0-6) of the first display day
    // convert to Luxon weekday (1-7): ISO 0-5 → Luxon 1-6, ISO 6 → Luxon 7
    const isoFirst = displayOrder[0];
    return isoFirst === 6 ? 7 : isoFirst + 1;
  }

  // calculates the start of the display week for a given date
  private getDisplayWeekStart(date: DateTime): DateTime {
    const targetFirstDay = this.getFirstDayOfWeek();
    const currentWeekday = date.weekday; // 1=Mon, 7=Sun
    // calculate days to go back to reach the first day of the display week
    const daysBack = (currentWeekday - targetFirstDay + 7) % 7;
    return date.minus({ days: daysBack });
  }

  ngOnInit() {
    const params = this.navParams.data;
    this.title = params?.title || 'Select Date';
    this.description = params?.description || '';
    // parse ISO string to DateTime
    this.selectedDate = params?.selectedDateISO
      ? DateTime.fromISO(params.selectedDateISO)
      : this.debug.getNowLocal().startOf('day');
    this.taskStatusLoader = this.navParams.data.local?.taskStatusLoader;

    const today = this.debug.getNowLocal().startOf('day');

    // if no selected date, use today
    if (!this.selectedDate) {
      this.selectedDate = today;
    }

    // initialize with selected date's month and surrounding months
    const startMonth = this.selectedDate.startOf('month');
    this.earliestMonth = startMonth.minus({ months: this.INITIAL_MONTHS_TO_LOAD });
    this.latestMonth = startMonth.plus({ months: this.INITIAL_MONTHS_TO_LOAD });
  }

  async ionViewWillEnter() {
    // #4249: refresh the day markers when the task-status cache changes —
    // most importantly when a log entry synced in from another device updates
    // a day's dot while this month grid is open.
    //
    // Subscribe only once per component instance. ionViewWillEnter fires again
    // every time the user comes back from a pushed child page, and the
    // unsubscribe lives in ngOnDestroy — so without this guard each re-entry
    // would overwrite the handle, leak the previous registration, and run one
    // extra full refresh per event. Same guard the Training page uses.
    if (!this.taskStatusCacheUpdatedReg) {
      this.taskStatusCacheUpdatedReg = this.pubSub.subscribe(TaskStatusCacheUpdatedEvent, (range) => {
        if (this.viewDestroyed) return;
        void this.refreshTaskStatusesAsync(range);
      });
    }

    // start loading essential months before animation (don't await - let it run during animation)
    const selectedMonth = this.selectedDate.startOf('month');
    this.essentialStart = selectedMonth.minus({ months: 1 });
    this.essentialEnd = selectedMonth.plus({ months: 1 });
    this.essentialLoadingPromise = this.generateMonthsForRange(this.essentialStart, this.essentialEnd);
  }

  async ionViewDidEnter() {
    // await essential months that started loading in ionViewWillEnter
    if (this.essentialLoadingPromise) {
      await this.essentialLoadingPromise;
      this.essentialLoadingPromise = null;
    }

    // yield to event loop to allow cs-content's spinner timer callback to execute
    await waitForAngularUpdate(this.injector);
    // each of these waits now returns when this page is destroyed instead of hanging
    // (df-3f0eaff27ac6), so EVERY one of them needs its own check after it — a destroy
    // during the second wait reaches setupIntersectionObserver just as surely as one
    // during the first. Everything below is view work on a page nobody is looking at
    // any more, and the observer would be left behind: ngOnDestroy has already had its
    // one chance to disconnect it
    if (this.viewDestroyed) return;

    // hide loading spinner - essential content is ready
    this.loading = false;
    this.safeMarkForCheck(this.cd);

    // wait for render, then scroll to selected date
    await waitForAngularUpdate(this.injector);
    if (this.viewDestroyed) return;
    // suspend scroll-hide detection during programmatic scroll to prevent header from hiding
    this.scrollHideService.suspendUntilNextFrame();
    this.scrollToSelectedDate();

    // set up intersection observer for infinite scrolling
    this.setupIntersectionObserver();

    // load remaining months in background (don't await)
    void this.loadRemainingMonths(this.essentialStart, this.essentialEnd);
  }

  private async loadRemainingMonths(essentialStart: DateTime, essentialEnd: DateTime) {
    // Load earlier months (prepend) - need to preserve scroll position
    if (essentialStart > this.earliestMonth) {
      await this.generateMonthsForRange(this.earliestMonth, essentialStart.minus({ months: 1 }), true);
      if (this.viewDestroyed) return;
      this.safeMarkForCheck(this.cd);

      // after DOM updates, scroll back to selected date to maintain position
      await waitForAngularUpdate(this.injector);
      if (this.viewDestroyed) return;
      this.scrollHideService.suspendUntilNextFrame();
      this.scrollToSelectedDate();
    }

    // Load later months (append) - no scroll adjustment needed
    if (essentialEnd < this.latestMonth) {
      await this.generateMonthsForRange(essentialEnd.plus({ months: 1 }), this.latestMonth, false);
      if (this.viewDestroyed) return;
      this.safeMarkForCheck(this.cd);
    }
  }

  ngOnDestroy() {
    // cleanup intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.taskStatusCacheUpdatedReg) {
      this.taskStatusCacheUpdatedReg.unsubscribe();
      this.taskStatusCacheUpdatedReg = undefined;
    }
  }

  /**
   * Re-read the day markers for the days the task-status cache just changed,
   * and update the already-rendered cells in place.
   *
   * Updating in place rather than rebuilding `this.months` matters: this page
   * is an infinitely scrolling grid of up to two years of months, and rebuilding
   * it would throw away the user's scroll position while they are looking at it.
   *
   * The page keeps its own `taskStatusCache` and only asks the loader for days
   * it does not already hold, so the changed days must be dropped from it first
   * — otherwise the reload would re-serve exactly the stale values this refresh
   * exists to replace.
   *
   * A change that touches none of the days this page currently shows is skipped
   * outright (#4451). That is safe because `taskStatusCache` is only ever filled
   * from loads bounded by `earliestMonth`..`latestMonth`, and those two only ever
   * grow — so a day outside the reload span is a day this page holds no value
   * for and draws nothing from.
   */
  private async refreshTaskStatusesAsync(change?: TaskStatusChangeRange) {
    if (!this.taskStatusLoader || this.months.length === 0) return;

    // the whole span this page currently shows
    const reloadStart = this.earliestMonth.startOf('month').startOf('week');
    const reloadEnd = this.latestMonth.endOf('month').endOf('week');

    // nothing that changed is on this page — skip. Before #4451 there was no
    // check here at all, so a backlog drain rebuilt this two-year grid on every
    // wave, for days years away from anything rendered.
    if (change && !taskStatusChangeAffectsVisibleDays(
      change, reloadStart.toISODate(), reloadEnd.toISODate())) {
      return;
    }

    // drop the changed days from the local map so the reload below re-reads
    // them; a null answer means "unknown", so drop everything
    const spans = taskStatusChangedSpans(change);
    if (spans) {
      for (const dateKey of [...this.taskStatusCache.keys()]) {
        if (spans.some(span => dateKey >= span.startDate && dateKey <= span.endDate)) {
          this.taskStatusCache.delete(dateKey);
        }
      }
    } else {
      this.taskStatusCache.clear();
    }

    try {
      const statuses = await this.taskStatusLoader(reloadStart, reloadEnd);
      statuses.forEach((status, date) => this.taskStatusCache.set(date, status));
    } catch (error) {
      // a silently failing refresh leaves stale dots on screen, so the failure
      // has to reach telemetry — console alone is invisible in production
      // a wrapper that is being TORN DOWN means the page is going away with the
      // database, so this is expected rather than a broken refresh. An
      // unavailable database on its own would be too broad — a transient
      // `Recovering` IS worth a record — which is what `isDatabaseTornDown`
      // narrows. Do NOT swap this for `isIntentionalDestruction()`: that flag is
      // raised at the WIPE, which happens after the shutdown that produces these
      // errors, so it is still false here. The console line moves with the
      // report: the E2E gate fails on any console.error that is not allow-listed.
      if (isDatabaseTornDown(error)) {
        console.log(`Task status refresh skipped — our own teardown (${error.reason})`);
      } else {
        console.error('Failed to refresh task statuses on cache update:', error);
        this.logging.trackSilentProblem('TaskCalendarStatusRefreshFailed', errorReportFields(error));
      }
      return;
    }
    if (this.viewDestroyed) return;

    // push the fresh values onto the cells already on screen
    for (const month of this.months) {
      for (const week of month.weeks) {
        for (const day of week.days) {
          day.taskStatus = this.taskStatusCache.get(day.dateKey) || TaskStatus.NoTask;
        }
      }
    }
    this.safeMarkForCheck(this.cd);
  }

  private setupIntersectionObserver() {
    // Get the actual scrollable element from cs-content
    const scrollElement = this.csContent?.getScrollRootElement() ?? null;

    const options = {
      root: scrollElement,
      rootMargin: '100px',
      threshold: 0
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoadingMore) {
          if (entry.target === this.topSentinel?.nativeElement) {
            void this.loadEarlierMonths();
          } else if (entry.target === this.bottomSentinel?.nativeElement) {
            void this.loadLaterMonths();
          }
        }
      });
    }, options);

    // start observing sentinels
    if (this.topSentinel?.nativeElement) {
      this.intersectionObserver.observe(this.topSentinel.nativeElement);
    }
    if (this.bottomSentinel?.nativeElement) {
      this.intersectionObserver.observe(this.bottomSentinel.nativeElement);
    }
  }

  private async loadEarlierMonths() {
    if (this.isLoadingMore) return;
    this.isLoadingMore = true;

    try {
      const MONTHS_TO_LOAD = 6;
      const newEarliestMonth = this.earliestMonth.minus({ months: MONTHS_TO_LOAD });

      await this.generateMonthsForRange(newEarliestMonth, this.earliestMonth.minus({ months: 1 }), true);
      this.earliestMonth = newEarliestMonth;
      this.safeMarkForCheck(this.cd);
    } finally {
      this.isLoadingMore = false;
    }
  }

  private async loadLaterMonths() {
    if (this.isLoadingMore) return;
    this.isLoadingMore = true;

    try {
      const MONTHS_TO_LOAD = 6;
      const newLatestMonth = this.latestMonth.plus({ months: MONTHS_TO_LOAD });

      await this.generateMonthsForRange(this.latestMonth.plus({ months: 1 }), newLatestMonth, false);
      this.latestMonth = newLatestMonth;
      this.safeMarkForCheck(this.cd);
    } finally {
      this.isLoadingMore = false;
    }
  }

  private async generateMonthsForRange(startMonth: DateTime, endMonth: DateTime, prepend: boolean = false) {
    const today = this.debug.getNowLocal().startOf('day');
    const newMonths: CalendarMonth[] = [];

    // load task statuses for the entire range if we have a loader function
    if (this.taskStatusLoader) {
      const rangeStart = startMonth.startOf('month').startOf('week');
      const rangeEnd = endMonth.endOf('month').endOf('week');

      const uncachedDates: string[] = [];
      let checkDate = rangeStart;
      while (checkDate <= rangeEnd) {
        const dateKey = checkDate.toISODate();
        if (!this.taskStatusCache.has(dateKey)) {
          uncachedDates.push(dateKey);
        }
        checkDate = checkDate.plus({ days: 1 });
      }

      if (uncachedDates.length > 0) {
        try {
          const newStatuses = await this.taskStatusLoader(rangeStart, rangeEnd);
          newStatuses.forEach((status, date) => {
            this.taskStatusCache.set(date, status);
          });
        } catch (error) {
          // if the load fails we still draw the calendar below, and every
          // uncached day falls back to `NoTask` — which looks exactly like a
          // month the user really did nothing in. So the failure has to reach
          // telemetry, or nobody ever learns it happened: a console line is
          // invisible in production. Same split as `refreshTaskStatusesAsync`
          // above: our own teardown is expected, everything else is a problem.
          if (isDatabaseTornDown(error)) {
            console.log(`Task status load skipped — our own teardown (${error.reason})`);
          } else {
            console.error('Failed to load task statuses:', error);
            this.logging.trackSilentProblem('TaskCalendarStatusRefreshFailed', errorReportFields(error));
          }
        }
      }
    }

    let currentMonth = startMonth.startOf('month');
    const lastMonth = endMonth.startOf('month');

    while (currentMonth <= lastMonth) {
      const month: CalendarMonth = {
        monthId: formatMonthKey(currentMonth),
        month: currentMonth,
        monthName: formatMonthLong(currentMonth, BOOT_LOCALE),
        year: currentMonth.year,
        weeks: []
      };

      let weekStart = this.getDisplayWeekStart(currentMonth);
      const monthEnd = currentMonth.endOf('month');
      const firstDayOfWeek = this.getFirstDayOfWeek();

      while (weekStart <= monthEnd || weekStart.weekNumber === monthEnd.weekNumber) {
        const week: CalendarWeek = { days: [] };

        for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
          const dayDate = weekStart.plus({ days: dayIndex });
          const dateKey = dayDate.toISODate();
          const taskStatus = this.taskStatusCache.get(dateKey) || TaskStatus.NoTask;

          week.days.push({
            date: dayDate,
            dateKey: dateKey,
            taskStatus: taskStatus,
            isSelected: dayDate.hasSame(this.selectedDate, 'day'),
            isToday: dayDate.hasSame(today, 'day'),
            isOutOfMonth: !dayDate.hasSame(currentMonth, 'month')
          });
        }

        month.weeks.push(week);
        weekStart = weekStart.plus({ weeks: 1 });

        // break if we've moved to next month and we're at the start of a new display week
        if (weekStart.month !== currentMonth.month && weekStart.weekday === firstDayOfWeek) {
          break;
        }
      }

      newMonths.push(month);
      currentMonth = currentMonth.plus({ months: 1 });
    }

    if (prepend) {
      this.months = [...newMonths, ...this.months];
    } else {
      this.months = [...this.months, ...newMonths];
    }
  }

  private scrollToSelectedDate() {
    // find the month that contains the selected date
    const selectedMonth = this.months.find(month =>
      month.weeks.some(week => week.days.some(day => day.isSelected && !day.isOutOfMonth))
    );

    if (!selectedMonth) return;

    // find the month element using the monthId as a data attribute
    const monthElement = document.querySelector(
      `page-task-calendar .month[data-month-id="${selectedMonth.monthId}"]`
    ) as HTMLElement;

    if (monthElement) {
      monthElement.scrollIntoView({
        behavior: 'auto',
        block: 'start',
        inline: 'start'
      });
    }
  }

  onDateClick(date: DateTime) {
    // update selected date in all months
    this.months.forEach(month => {
      month.weeks.forEach(week => {
        week.days.forEach(day => {
          day.isSelected = day.date.hasSame(date, 'day');
        });
      });
    });

    this.selectedDate = date;
    this.cd.markForCheck();

    // close the page with the selected date
    void this.returnAsync({ selectedDate: this.selectedDate });
  }

  onToday() {
    const today = this.debug.getNowLocal().startOf('day');
    this.onDateClick(today);
  }

  async onCloseClick() {
    await this.returnAsync({ selectedDate: null });
  }

  trackByMonthId(_index: number, month: CalendarMonth): string {
    return month.monthId;
  }
}
